import { Badge, Divider, Space, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import {
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LinkButton, ScopeButton } from '../components/buttons';
import { FilterSelect } from '../components/formSelect';
import { ACCOUNT_TYPE_LABEL_KEYS, resolveAccountType, type SharedUser } from './types';

export type UsersFilterState = {
  accountType?: string[];
  accountActivated?: string[];
  accountCreatorName?: string[];
};

type UsersTableFiltersProps = {
  members: SharedUser[];
  value: UsersFilterState;
  onChange: (next: UsersFilterState) => void;
};

type FilterOption = {
  value: string;
  label: ReactNode;
  title: string;
};

type UsersFilterPillProps = {
  placeholder: string;
  minWidth: number;
  committed?: string[];
  options: FilterOption[];
  showSearch?: boolean;
  onApply: (next?: string[]) => void;
};

const ClearFiltersButton = styled(LinkButton)`
  text-decoration: none !important;
  color: var(--color-text-base, #042a2a) !important;

  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus {
    color: var(--color-primary, #41aaaf) !important;
    background: transparent !important;
    text-decoration: none !important;
  }

  > span {
    text-decoration: none !important;
  }
`;

const filterSelectPopup = {
  popup: { root: 'scope-table-filter-dropdown users-filter-dropdown' },
};

const filterPopupStyle: CSSProperties = {
  minWidth: 420,
  width: 'max-content',
  maxWidth: '95vw',
  padding: '4px 16px 16px',
  fontSize: 13,
  borderRadius: 20,
};

const hasValues = (values?: string[]) => Boolean(values && values.length > 0);

export const applyUsersFilters = (
  members: SharedUser[],
  filters: UsersFilterState,
): SharedUser[] =>
  members.filter((member) => {
    if (
      hasValues(filters.accountType) &&
      !filters.accountType!.includes(member.accountType)
    ) {
      return false;
    }
    if (hasValues(filters.accountActivated)) {
      const status = member.accountActivated ? 'true' : 'false';
      if (!filters.accountActivated!.includes(status)) {
        return false;
      }
    }
    if (
      hasValues(filters.accountCreatorName) &&
      !filters.accountCreatorName!.includes(member.accountCreatorName ?? '')
    ) {
      return false;
    }
    return true;
  });

const UsersFilterPill = ({
  placeholder,
  minWidth,
  committed,
  options,
  showSearch = true,
  onApply,
}: UsersFilterPillProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const selected = committed ?? [];

  return (
    <Badge
      count={selected.length}
      overflowCount={99}
      style={{ backgroundColor: 'var(--color-primary, #1b1055)' }}
    >
      <FilterSelect
        mode="multiple"
        height="45px"
        className={selected.length ? 'selected-filter' : undefined}
        classNames={filterSelectPopup}
        popupClassName="scope-table-filter-dropdown users-filter-dropdown"
        styles={{ popup: { root: filterPopupStyle } }}
        dropdownMatchSelectWidth={false}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ minWidth, width: 'auto' }}
        maxTagCount={0}
        tagRender={() => <></>}
        options={options}
        virtual={false}
        listHeight={256}
        showSearch={showSearch}
        filterOption={(input, option) => {
          const optionRow = option as DefaultOptionType | undefined;
          const haystack = String(
            optionRow?.title ?? optionRow?.value ?? '',
          ).toLowerCase();
          return haystack.includes(input.toLowerCase());
        }}
        value={open ? draft : []}
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setDraft(selected);
          }
          setOpen(next);
        }}
        onChange={(val) => setDraft((val as string[]) ?? [])}
        popupRender={(items) => (
          <div
            className="users-filter-dropdown-body"
            style={{ minWidth: 380, width: 'max-content', maxWidth: '90vw' }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Space
              direction="vertical"
              size={0}
              className="users-filter-dropdown-stack"
              style={{ width: '100%' }}
            >
              {items}
              <Divider style={{ margin: '8px 0' }} />
              <Space style={{ float: 'inline-end', margin: '0 10px' }}>
                <ScopeButton
                  width="75px"
                  height="25px"
                  fontSize="12px"
                  lineheight="12px"
                  type="default"
                  onClick={() => {
                    setDraft([]);
                    onApply(undefined);
                    setOpen(false);
                  }}
                >
                  {t('common.clear')}
                </ScopeButton>
                <ScopeButton
                  width="75px"
                  height="25px"
                  fontSize="12px"
                  lineheight="12px"
                  type="primary"
                  onClick={() => {
                    onApply(draft.length ? draft : undefined);
                    setOpen(false);
                  }}
                >
                  {t('common.apply')}
                </ScopeButton>
              </Space>
            </Space>
          </div>
        )}
        getPopupContainer={() => document.body}
        popupMatchSelectWidth={false}
      />
    </Badge>
  );
};

export const UsersTableFilters = ({
  members,
  value,
  onChange,
}: UsersTableFiltersProps) => {
  const { t } = useTranslation();

  const roleOptions = useMemo<FilterOption[]>(() => {
    const keys = Array.from(
      new Set(members.map((m) => m.accountType).filter(Boolean)),
    );
    return keys.map((key) => {
      const label = ACCOUNT_TYPE_LABEL_KEYS[key]
        ? t(ACCOUNT_TYPE_LABEL_KEYS[key])
        : resolveAccountType(key) || key;
      return { value: key, label, title: label };
    });
  }, [members, t]);

  const statusOptions = useMemo<FilterOption[]>(
    () => [
      { value: 'true', label: t('common.active'), title: t('common.active') },
      {
        value: 'false',
        label: t('users.pending-activation'),
        title: t('users.pending-activation'),
      },
    ],
    [t],
  );

  const creatorOptions = useMemo<FilterOption[]>(() => {
    const names = Array.from(
      new Set(members.map((m) => m.accountCreatorName).filter(Boolean)),
    ) as string[];
    return names.map((name) => ({ value: name, label: name, title: name }));
  }, [members]);

  const hasFilters =
    hasValues(value.accountType) ||
    hasValues(value.accountActivated) ||
    hasValues(value.accountCreatorName);

  return (
    <Space wrap size={8} className="users-table-filters" style={{ flex: '1 1 auto', minWidth: 0 }}>
      <Typography
        style={{
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '12px',
          textAlign: 'left',
          color: 'var(--color-text-base, #042a2a)',
          minWidth: 35,
        }}
      >
        {t('main.filters')}
      </Typography>
      <UsersFilterPill
        placeholder={t('users.accountType')}
        minWidth={150}
        committed={value.accountType}
        options={roleOptions}
        onApply={(next) => onChange({ ...value, accountType: next })}
      />
      <UsersFilterPill
        placeholder={t('users.accountActivated')}
        minWidth={150}
        committed={value.accountActivated}
        options={statusOptions}
        showSearch={false}
        onApply={(next) => onChange({ ...value, accountActivated: next })}
      />
      <UsersFilterPill
        placeholder={t('users.accountCreatorName')}
        minWidth={150}
        committed={value.accountCreatorName}
        options={creatorOptions}
        onApply={(next) => onChange({ ...value, accountCreatorName: next })}
      />
      {hasFilters && (
        <ClearFiltersButton type="link" onClick={() => onChange({})}>
          {t('main.clear-filters')}
        </ClearFiltersButton>
      )}
    </Space>
  );
};
